import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { IoCameraOutline } from 'react-icons/io5';
import { CachedImage, DotMenu, DoubleCheckModal } from '@shared/ui';
import { useHideSection, useDeleteSection } from '../../hooks/useSections';
import type { Section } from '../../types';
import './SectionCard.css';

interface SectionCardProps {
  section: Section;
  onEdit?: () => void;
  selected?: boolean;
  compact?: boolean;
}

export default function SectionCard({ section, onEdit, selected, compact }: SectionCardProps) {
  const navigate = useNavigate();
  const hideSection = useHideSection();
  const deleteSection = useDeleteSection();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      navigate(`/sections/${section.id}/edit`);
    }
  };

  const handleHide = async () => {
    try {
      await hideSection.mutateAsync({
        id: section.id,
        isHidden: !section.isHidden,
      });
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при изменении видимости секции',
        color: 'red',
      });
    }
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    await deleteSection.mutateAsync(section.id);
    setIsDeleteModalOpen(false);
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const menuItems = [
    {
      label: 'Редактировать',
      onClick: handleEdit,
    },
    {
      label: section.isHidden ? 'Показать' : 'Скрыть',
      onClick: handleHide,
    },
    {
      label: 'Удалить',
      onClick: handleDelete,
      isDanger: true,
    },
  ];

  return (
    <div
      className={`section-card-wrapper${compact ? ' section-card-wrapper--compact' : ''}${selected ? ' section-card-wrapper--selected' : ''}`}
    >
      <Link to={`/sections/${section.id}/categories`} className="section-card">
        <div className={`section-card-image ${section.isHidden ? 'section-card-image--hidden' : ''} ${!section.imageUrl ? 'section-card-image--placeholder' : ''}`}>
          {section.imageUrl ? (
            <CachedImage src={section.imageUrl} alt={section.name} loading="lazy" />
          ) : (
            <div className="section-card-camera-icon">
              <IoCameraOutline />
            </div>
          )}
          {section.isHidden && (
            <div className="section-card-badges">
              <span className="section-card-hidden-badge">🔒</span>
            </div>
          )}
        </div>
        <div className="section-card-content">
          <h3 className="section-card-title">{section.name}</h3>
        </div>
      </Link>
      <div className="section-card-menu">
        <DotMenu items={menuItems} />
      </div>

      {isDeleteModalOpen && <DoubleCheckModal
        isOpen={isDeleteModalOpen}
        onClose={cancelDelete}
        title="Удалить секцию?"
        message={`Вы уверены, что хотите удалить секцию "${section.name}"?`}
        secondButtonText="Удалить"
        secondButtonAction={confirmDelete}
        isLoading={deleteSection.isPending}
      />}
    </div>
  );
}

